// src/utils/ordenPedidoPDFGenerator.js

// =====================================================
// HELPERS DE FECHA Y NÚMEROS
// =====================================================
const formatearFecha = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const d = new Date(fechaISO);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yy = d.getFullYear();
    const hh = d.getHours().toString().padStart(2, '0');
    const mi = d.getMinutes().toString().padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return fechaISO; }
};

const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return '—';
  try {
    const d = new Date(fechaISO);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  } catch { return fechaISO; }
};

const formatearNumero = (valor, decimales = 2) => {
  if (valor === null || valor === undefined || valor === '') return '0.00';
  const num = Number(valor);
  if (isNaN(num)) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }).format(num);
};

const formatearCodigoEntrega = (codigo) => {
  if (!codigo) return '------';
  return String(codigo).padStart(6, '0');
};

// =====================================================
// GENERAR HTML DE LA ORDEN (COMPACTA Y MODERNA)
// =====================================================
export const generarHTMLOrdenPedido = (pedido, empresa = {}) => {
  const numeroPedido = pedido.numeroPedido || 'N/A';
  const fechaActual = new Date().toISOString();

  const nombreCliente = `${pedido.nombre || ''} ${pedido.apellido || ''}`.trim() || 'CLIENTE';
  const cedula = pedido.cedula || '—';
  const telefono = pedido.telefono || '—';
  const direccion = pedido.direccion || '—';
  const puntoReferencia = pedido.puntoReferencia || '';
  const urlMapa = pedido.urlMapa || '';

  const codigoEntrega = pedido.codigoEntrega || null;

  const servicios = pedido.servicios || [];
  const totalBs = Number(pedido.totalBs || 0);

  const nombreEmpresa = empresa.nombre || 'EXTRA GAS';
  const rifEmpresa = empresa.rif || 'J-41023119-0';
  const telefonoEmpresa = empresa.telefono || '0424-5976200';
  const direccionEmpresa = empresa.direccion || 'Caracas, Venezuela';
  const ciudadEmpresa = empresa.ciudad || '';
  const emailEmpresa = empresa.email || '';
  const logoEmpresa = empresa.logoBase64 || '';

  const filasServicios = servicios.length > 0
    ? servicios.map((s, i) => `
        <tr>
          <td class="text-center">${i + 1}</td>
          <td>
            <div class="producto-nombre">${s.nombre}</div>
            ${s.kilogramos ? `<div class="producto-detalle">${s.kilogramos} kg</div>` : ''}
          </td>
          <td class="text-center">${s.cantidad}</td>
          <td class="text-right">Bs. ${formatearNumero(s.precioBs)}</td>
          <td class="text-right bold">Bs. ${formatearNumero(s.subtotalBs)}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="5" class="text-center vacio">Sin servicios</td></tr>`;

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Orden ${numeroPedido}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: 'Segoe UI', 'Helvetica', 'Arial', sans-serif;
          background: #eef2f7;
          color: #1a2a4a;
          padding: 12px;
          font-size: 11px;
          line-height: 1.35;
        }

        .orden {
          max-width: 720px;
          margin: 0 auto;
          background: white;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 12px 32px rgba(26, 42, 74, 0.12);
        }

        /* ═══════ HEADER COMPACTO ═══════ */
        .header {
          background: linear-gradient(135deg, #1a2a4a 0%, #2c3e5c 55%, #E87A30 100%);
          padding: 14px 20px;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          position: relative;
          overflow: hidden;
        }
        .header::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
          border-radius: 50%;
        }
        .empresa-info {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
          min-width: 0;
        }
        .empresa-logo {
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.18);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 1.5px solid rgba(255, 255, 255, 0.25);
          flex-shrink: 0;
          font-size: 20px;
        }
        .empresa-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .empresa-nombre {
          font-size: 15px;
          font-weight: 900;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .empresa-slogan {
          font-size: 9px;
          opacity: 0.85;
          margin-top: 2px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 700;
        }
        .orden-numero-box {
          text-align: right;
          position: relative;
          z-index: 1;
          flex-shrink: 0;
        }
        .orden-numero-label {
          font-size: 8px;
          opacity: 0.8;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .orden-numero-valor {
          font-size: 20px;
          font-weight: 900;
          letter-spacing: 0.02em;
          line-height: 1;
        }

        /* ═══════ CÓDIGO DE ENTREGA COMPACTO ═══════ */
        .codigo-entrega-wrapper {
          padding: 12px 20px;
          background: linear-gradient(135deg, #0f1a2f 0%, #1a2a4a 100%);
          position: relative;
          overflow: hidden;
        }
        .codigo-entrega-wrapper::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -10%;
          width: 220px;
          height: 220px;
          background: radial-gradient(circle, rgba(39, 174, 96, 0.15) 0%, transparent 70%);
          border-radius: 50%;
        }
        .codigo-entrega-box {
          position: relative;
          z-index: 1;
          border: 2px dashed #27AE60;
          border-radius: 10px;
          padding: 10px;
          text-align: center;
          background: rgba(39, 174, 96, 0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }
        .codigo-entrega-titulo {
          font-size: 9px;
          font-weight: 900;
          color: #27AE60;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          text-align: left;
          line-height: 1.4;
        }
        .codigo-entrega-titulo .lock {
          font-size: 16px;
          display: block;
          margin-bottom: 2px;
        }
        .codigo-entrega-valor {
          font-family: 'Courier New', monospace;
          font-size: 30px;
          font-weight: 900;
          color: #27AE60;
          letter-spacing: 0.25em;
          line-height: 1;
          text-shadow: 0 0 20px rgba(39, 174, 96, 0.5);
          flex: 1;
          text-align: center;
        }
        .codigo-entrega-aviso {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          font-size: 8px;
          font-weight: 900;
          color: #FBBF24;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-align: center;
          max-width: 90px;
          line-height: 1.3;
        }
        .codigo-entrega-aviso .icon {
          font-size: 14px;
        }

        /* ═══════ INFO GRID COMPACTO ═══════ */
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          border-bottom: 1px solid #eef2f7;
        }
        .info-col {
          padding: 12px 20px;
        }
        .info-col:first-child {
          border-right: 1px solid #eef2f7;
        }
        .info-titulo {
          font-size: 9px;
          font-weight: 900;
          color: #E87A30;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .info-linea {
          font-size: 10px;
          color: #4a5568;
          margin-bottom: 3px;
          font-weight: 600;
          word-break: break-word;
          line-height: 1.4;
        }
        .info-linea strong {
          color: #1a2a4a;
          font-weight: 900;
        }

        /* ═══════ CLIENTE COMPACTO ═══════ */
        .cliente-box {
          padding: 12px 20px;
          background: linear-gradient(135deg, #f8fafc 0%, #f0f4fa 100%);
          border-bottom: 1px solid #eef2f7;
        }
        .cliente-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }
        .cliente-header-icon {
          width: 22px;
          height: 22px;
          border-radius: 7px;
          background: linear-gradient(135deg, #5DADE2 0%, #3a8fc4 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          flex-shrink: 0;
        }
        .cliente-header-titulo {
          font-size: 9px;
          font-weight: 900;
          color: #1a2a4a;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .cliente-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 16px;
        }
        .cliente-item {
          font-size: 10px;
          min-width: 0;
        }
        .cliente-item-label {
          font-size: 8px;
          font-weight: 900;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 2px;
        }
        .cliente-item-valor {
          font-size: 11px;
          font-weight: 800;
          color: #1a2a4a;
          word-break: break-word;
          line-height: 1.3;
        }

        /* ═══════ TABLA COMPACTA ═══════ */
        .tabla-wrapper {
          padding: 12px 20px;
        }
        .tabla-titulo {
          font-size: 9px;
          font-weight: 900;
          color: #1a2a4a;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          border-radius: 8px;
          overflow: hidden;
        }
        thead {
          background: linear-gradient(135deg, #1a2a4a 0%, #2c3e5c 100%);
        }
        th {
          padding: 7px 8px;
          font-size: 9px;
          font-weight: 900;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          text-align: left;
        }
        th.text-center { text-align: center; }
        th.text-right { text-align: right; }
        tbody tr {
          border-bottom: 1px solid #eef2f7;
        }
        tbody tr:last-child {
          border-bottom: none;
        }
        tbody tr:nth-child(even) {
          background: #f8fafc;
        }
        td {
          padding: 7px 8px;
          font-size: 10px;
          color: #1a2a4a;
          font-weight: 600;
          vertical-align: top;
        }
        td.text-center { text-align: center; }
        td.text-right { text-align: right; }
        td.bold { font-weight: 900; color: #E87A30; }
        .producto-nombre {
          font-weight: 900;
          color: #1a2a4a;
          font-size: 11px;
          line-height: 1.2;
        }
        .producto-detalle {
          font-size: 9px;
          color: #94a3b8;
          font-weight: 600;
          margin-top: 1px;
        }
        .vacio {
          text-align: center;
          color: #94a3b8;
          font-style: italic;
          padding: 12px;
        }

        /* ═══════ TOTALES COMPACTOS ═══════ */
        .totales-wrapper {
          padding: 0 20px 12px;
          display: flex;
          justify-content: flex-end;
        }
        .totales-box {
          width: 100%;
          max-width: 260px;
          background: linear-gradient(135deg, #1a2a4a 0%, #2c3e5c 100%);
          border-radius: 10px;
          padding: 12px 16px;
          color: white;
          box-shadow: 0 8px 20px rgba(26, 42, 74, 0.2);
        }
        .total-linea {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;
          font-size: 10px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.85);
        }
        .total-linea.grande {
          border-top: 1.5px solid rgba(255, 255, 255, 0.2);
          margin-top: 4px;
          padding-top: 8px;
        }
        .total-linea.grande span:first-child {
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: white;
        }
        .total-linea.grande span:last-child {
          font-size: 17px;
          font-weight: 900;
          color: #FF9F5A;
          letter-spacing: -0.02em;
        }

        /* ═══════ INFO EXTRA COMPACTA ═══════ */
        .info-extra {
          padding: 10px 20px;
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
          border-top: 1.5px solid #fed7aa;
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .info-extra-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          font-size: 14px;
        }
        .info-extra-titulo {
          font-size: 9px;
          font-weight: 900;
          color: #9a3412;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 2px;
        }
        .info-extra-desc {
          font-size: 10px;
          color: #7c2d12;
          line-height: 1.4;
          font-weight: 600;
        }

        /* ═══════ FOOTER COMPACTO ═══════ */
        .footer {
          background: #0f1a2f;
          padding: 10px 20px;
          color: white;
          text-align: center;
        }
        .footer-titulo {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .footer-datos {
          font-size: 9px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
          font-weight: 500;
        }
        .footer-datos strong {
          color: #FF9F5A;
          font-weight: 900;
        }

        /* ═══════ BOTONES ═══════ */
        .acciones {
          text-align: center;
          margin-top: 14px;
          padding: 10px;
        }
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 18px;
          border: none;
          border-radius: 9px;
          font-family: inherit;
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          cursor: pointer;
          transition: all 0.25s ease;
          margin: 0 4px;
        }
        .btn-print {
          background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
          color: white;
          box-shadow: 0 6px 18px rgba(232, 122, 48, 0.35);
        }
        .btn-print:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(232, 122, 48, 0.5);
        }
        .btn-close {
          background: white;
          color: #1a2a4a;
          border: 2px solid #e2e8f0;
        }
        .btn-close:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        /* ═══════ IMPRESIÓN ═══════ */
        @media print {
          body {
            background: white;
            padding: 0;
            font-size: 10px;
          }
          .orden {
            box-shadow: none;
            border-radius: 0;
            max-width: 100%;
          }
          .acciones {
            display: none;
          }
          @page {
            size: A4;
            margin: 0.8cm;
          }
          .header, .footer, thead, .totales-box, .codigo-entrega-wrapper, .codigo-entrega-box {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>

      <div class="orden">

        <!-- HEADER -->
        <div class="header">
          <div class="empresa-info">
            <div class="empresa-logo">
              ${logoEmpresa ? `<img src="${logoEmpresa}" alt="Logo">` : '🔥'}
            </div>
            <div>
              <div class="empresa-nombre">${nombreEmpresa}</div>
              <div class="empresa-slogan">Recarga de Gas a Domicilio</div>
            </div>
          </div>
          <div class="orden-numero-box">
            <div class="orden-numero-label">N° de Pedido</div>
            <div class="orden-numero-valor">${numeroPedido}</div>
          </div>
        </div>

        <!-- CÓDIGO DE ENTREGA COMPACTO -->
        ${codigoEntrega ? `
        <div class="codigo-entrega-wrapper">
          <div class="codigo-entrega-box">
            <div class="codigo-entrega-titulo">
              <span class="lock">🔒</span>
              Código de<br>Entrega
            </div>
            <div class="codigo-entrega-valor">${formatearCodigoEntrega(codigoEntrega)}</div>
            <div class="codigo-entrega-aviso">
              <span class="icon">⚠️</span>
              Dáselo al delivery solo al recibir
            </div>
          </div>
        </div>
        ` : ''}

        <!-- INFO EMPRESA + ORDEN -->
        <div class="info-grid">
          <div class="info-col">
            <div class="info-titulo">🏢 Datos de la Empresa</div>
            <div class="info-linea"><strong>RIF:</strong> ${rifEmpresa}</div>
            <div class="info-linea"><strong>Dirección:</strong> ${direccionEmpresa}</div>
            ${ciudadEmpresa ? `<div class="info-linea"><strong>Ciudad:</strong> ${ciudadEmpresa}</div>` : ''}
            ${telefonoEmpresa ? `<div class="info-linea"><strong>Teléfono:</strong> ${telefonoEmpresa}</div>` : ''}
          </div>

          <div class="info-col">
            <div class="info-titulo">📅 Datos de la Orden</div>
            <div class="info-linea"><strong>N° Pedido:</strong> ${numeroPedido}</div>
            <div class="info-linea"><strong>Fecha:</strong> ${formatearFecha(fechaActual)}</div>
            <div class="info-linea"><strong>Estado:</strong> Pendiente por verificar</div>
            <div class="info-linea"><strong>Válida hasta:</strong> ${formatearFechaCorta(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}</div>
          </div>
        </div>

        <!-- CLIENTE -->
        <div class="cliente-box">
          <div class="cliente-header">
            <div class="cliente-header-icon">👤</div>
            <div class="cliente-header-titulo">Datos del Cliente</div>
          </div>
          <div class="cliente-grid">
            <div class="cliente-item">
              <div class="cliente-item-label">Nombre completo</div>
              <div class="cliente-item-valor">${nombreCliente}</div>
            </div>
            <div class="cliente-item">
              <div class="cliente-item-label">Cédula</div>
              <div class="cliente-item-valor">${cedula}</div>
            </div>
            <div class="cliente-item">
              <div class="cliente-item-label">Teléfono</div>
              <div class="cliente-item-valor">${telefono}</div>
            </div>
            <div class="cliente-item">
              <div class="cliente-item-label">Dirección</div>
              <div class="cliente-item-valor">${direccion}</div>
            </div>
            ${puntoReferencia ? `
            <div class="cliente-item" style="grid-column: 1 / -1;">
              <div class="cliente-item-label">Punto de referencia</div>
              <div class="cliente-item-valor">${puntoReferencia}</div>
            </div>
            ` : ''}
          </div>
        </div>

        <!-- TABLA SERVICIOS -->
        <div class="tabla-wrapper">
          <div class="tabla-titulo">🔥 Detalle de Servicios</div>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width: 30px;">#</th>
                <th>Descripción</th>
                <th class="text-center" style="width: 55px;">Cant.</th>
                <th class="text-right" style="width: 100px;">P. Unit.</th>
                <th class="text-right" style="width: 110px;">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              ${filasServicios}
            </tbody>
          </table>
        </div>

        <!-- TOTALES -->
        <div class="totales-wrapper">
          <div class="totales-box">
            <div class="total-linea">
              <span>Subtotal</span>
              <span>Bs. ${formatearNumero(totalBs)}</span>
            </div>
            <div class="total-linea">
              <span>Delivery</span>
              <span>Por definir</span>
            </div>
            <div class="total-linea grande">
              <span>Total</span>
              <span>Bs. ${formatearNumero(totalBs)}</span>
            </div>
          </div>
        </div>

        <!-- INFO EXTRA -->
        <div class="info-extra">
          <div class="info-extra-icon">ℹ️</div>
          <div>
            <div class="info-extra-titulo">Próximo Paso</div>
            <div class="info-extra-desc">
              Un operador te contactará al <strong>${telefono}</strong> para verificar el pago. Guarda tu N° <strong>${numeroPedido}</strong>${codigoEntrega ? ` y tu <strong>código de entrega</strong>` : ''}.
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="footer">
          <div class="footer-titulo">¡Gracias por tu preferencia!</div>
          <div class="footer-datos">
            <strong>${nombreEmpresa}</strong> · ${direccionEmpresa}${ciudadEmpresa ? ', ' + ciudadEmpresa : ''}<br>
            ${telefonoEmpresa ? 'Tel: ' + telefonoEmpresa : ''}${emailEmpresa ? ' · ' + emailEmpresa : ''}
          </div>
        </div>

      </div>

      <!-- ACCIONES -->
      <div class="acciones">
        <button class="btn btn-print" onclick="window.print()">
          🖨️ Imprimir / Guardar PDF
        </button>
        <button class="btn btn-close" onclick="window.close()">
          ✕ Cerrar
        </button>
      </div>

    </body>
    </html>
  `;
};

// =====================================================
// ABRIR VENTANA E IMPRIMIR
// =====================================================
export const imprimirOrdenPedidoPDF = async (pedido, empresa = {}) => {
  const html = generarHTMLOrdenPedido(pedido, empresa);
  const ventana = window.open('', '_blank', 'width=900,height=1000');

  if (!ventana) {
    alert('⚠️ Por favor permite ventanas emergentes para descargar la orden');
    return;
  }

  ventana.document.write(html);
  ventana.document.close();
  ventana.focus();

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 500);
  });
};

export default {
  generarHTMLOrdenPedido,
  imprimirOrdenPedidoPDF
};